import { useState, useEffect } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { logger } from '../../services/logger';

interface UserData {
  id: string;
  email: string;
  full_name: string;
  subscription_status: string;
  created_at: string;
  last_login: string;
  user_stats?: {
    total_vision_boards: number;
    total_goals: number;
    manifestation_score: number;
    ai_interactions: number;
    last_activity_date: string;
  };
}

export function UsersPage() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<{
    status?: string;
  }>({});
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    trialUsers: 0,
    averageScore: 0
  });

  useEffect(() => {
    loadUsers();
  }, [filter]);

  async function loadUsers() {
    try {
      setLoading(true);

      // Create a proper query chain
      let query = supabase
        .from('profiles')
        .select(`
          *,
          user_stats (
            total_vision_boards,
            total_goals,
            manifestation_score,
            ai_interactions,
            last_activity_date
          )
        `);

      // Apply filters if needed
      if (filter.status) {
        query = query.eq('subscription_status', filter.status);
      }
      
      // Order by creation date
      query = query.order('created_at', { ascending: false });

      // Execute the query
      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Calculate stats
      if (data) {
        const activeCount = data.filter((user: UserData) => user.subscription_status === 'active').length;
        const totalScore = data.reduce((sum: number, user: UserData) => sum + (user.user_stats?.manifestation_score || 0), 0);

        setStats({
          totalUsers: data.length,
          activeUsers: activeCount,
          trialUsers: 0,
          averageScore: data.length ? Math.round(totalScore / data.length) : 0
        });

        setUsers(data as UserData[]);
      }
    } catch (err: any) {
      logger.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }

  const exportUsers = () => {
    const csv = [
      ['Full Name', 'Email', 'Status', 'Joined', 'Vision Boards', 'Goals', 'Manifestation Score', 'AI Interactions', 'Last Active'].join(','),
      ...users.map(user => [
        user.full_name || '',
        user.email || '',
        user.subscription_status,
        new Date(user.created_at).toLocaleDateString(),
        user.user_stats?.total_vision_boards || 0,
        user.user_stats?.total_goals || 0,
        user.user_stats?.manifestation_score || 0,
        user.user_stats?.ai_interactions || 0,
        user.user_stats?.last_activity_date ? new Date(user.user_stats.last_activity_date).toLocaleDateString() : 'Never'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vzmo-users-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (user.full_name?.toLowerCase().includes(query) || false) ||
      (user.email?.toLowerCase().includes(query) || false)
    );
  });

  return (
    <div className="p-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500 mb-1">Total Users</div>
          <div className="text-2xl font-bold text-gray-900">{stats.totalUsers}</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500 mb-1">Active Users</div>
          <div className="text-2xl font-bold text-green-600">{stats.activeUsers}</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500 mb-1">Avg. Manifestation Score</div>
          <div className="text-2xl font-bold text-purple-600">{stats.averageScore}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Users</h1>
              <p className="text-gray-600">Manage and monitor user accounts</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={loadUsers}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <button
                onClick={exportUsers}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-4 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex gap-4">
              <select
                value={filter.status || ''}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value || undefined }))}
                className="rounded-lg border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vision Boards</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Goals</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-600 font-medium">
                            {user.full_name?.[0] || user.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name || 'No name'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.subscription_status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.subscription_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.user_stats?.total_vision_boards || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.user_stats?.total_goals || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.user_stats?.last_activity_date
                        ? new Date(user.user_stats.last_activity_date).toLocaleDateString()
                        : 'Never'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}